<!-- eslint-disable max-lines -->
<template>
  <FormSection>
    <Card>
      <div class="card__header">
        <strong>
          {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.CELLPHONE_VERIFICATION') }}
        </strong>
      </div>
      <div v-if="!smsHasSent">
        <p>
          {{
            $t('AUTH.STEPS.IDENTITY_VERIFICATION.MESSAGE_PHONE_CONFIRM_DESC', {
              phone,
            })
          }}
        </p>

        <ElButton
          icon="el-icon-chat-dot-square"
          type="primary"
          :loading="isSendingSms"
          @click.native="sendSms"
        >
          {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.SEND_CODE') }}
        </ElButton>

        <ElButton
          icon="el-icon-edit"
          @click.native="openModal('editPhoneModal')"
        >
          {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.EDIT_PHONE_BTN') }}
        </ElButton>
      </div>
      <template v-else>
        <div
          v-dompurify-html="
            smsHasSent
              ? $t('AUTH.STEPS.IDENTITY_VERIFICATION.MESSAGE_DESC', {
                  phone,
                })
              : $t('AUTH.STEPS.IDENTITY_VERIFICATION.MESSAGE_SENDING_DESC', {
                  phone,
                })
          "
        />
        <div v-if="errorMessage" class="text-alert">
          {{ errorMessage }}
        </div>

        <div class="security-code">
          <div class="security-code__input-wrapper">
            <ElInput
              v-model="code"
              type="numeric"
              maxlength="4"
              class="security-code__input"
              placeholder="####"
              dir="ltr"
            />
          </div>
        </div>

        <div class="auth-steps__navigation">
          <div class="security-code__actions">
            <ElButton
              icon="el-icon-refresh"
              :disabled="time > 0"
              :loading="isSendingSms"
              @click.native="sendSms"
            >
              {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.RESEND_BTN') }}
              <template v-if="time">:&nbsp;{{ time }}</template>
            </ElButton>

            <ElButton
              icon="el-icon-edit"
              @click.native="openModal('editPhoneModal')"
            >
              {{ $t('AUTH.STEPS.IDENTITY_VERIFICATION.EDIT_PHONE_BTN') }}
            </ElButton>
          </div>
          <ElButton
            type="primary"
            :loading="loading"
            @click.native="submitSmsVerificationForm"
          >
            {{ $t('REGISTER') }}
          </ElButton>
        </div>
      </template>
      <ErrorsList v-if="errors" :errors="errors" />
    </Card>

    <ChangePhoneNumberModal
      v-model="editPhoneModal"
      :phone="phone"
      :loading="isSendingSms"
      @changePhone="sendSmsToNewNumber"
    />
  </FormSection>
</template>

<script>
import { Register } from '@/api/api';
import storage from '@/services/storage';
import normalizeNumbers from '@/utils/normalizeNumbers';
import modal from '@/mixins/modal';
import form from '@/mixins/form';
import ErrorsList from '@@/common/ErrorsList';
import ChangePhoneNumberModal from './modal/ChangePhoneNumberModal';
import Card from '@@/common/Card';
import FormSection from '@@/common/layout/FormSection.vue';

const STARGE_DATA_KEY = 'registration-sms-sent';

export default {
  name: 'IdentifyVerification',

  components: {
    ErrorsList,
    ChangePhoneNumberModal,
    Card,
    FormSection,
  },

  mixins: [form, modal],

  props: {
    length: {
      type: Number,
      default: 4,
    },
    placeholder: {
      type: String,
      default: '-',
    },
    defaultPhone: {
      type: String,
      default: undefined,
    },
    errors: {
      type: Object,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      code: '',
      codeLength: 4,
      timer: null,
      time: null,
      isSendingSms: false,
      smsHasSent: false,
      editPhoneModal: false,
      errorMessage: undefined,
      smsSentNumber: localStorage.getItem('registration-sms-sent'),
      phone: this.defaultPhone && this.defaultPhone.replace(/\D+/g, ''),
    };
  },

  watch: {
    code(val) {
      this.code = normalizeNumbers(val);
    },
  },

  mounted() {
    if (this.phone) {
      storage.remove(STARGE_DATA_KEY);
      this.time = 0;
    } else {
      this.retrievePhoneAndState();
    }
  },

  methods: {
    retrievePhoneAndState() {
      const { phone, exp } = storage.readJSON(STARGE_DATA_KEY) || {};
      this.phone = phone;
      this.smsHasSent = Boolean(phone);

      const secondsRemain = Math.ceil((Number(exp || 0) - Date.now()) / 1000);
      if (secondsRemain > 0) {
        this.createTimer(secondsRemain);
      } else {
        storage.remove(STARGE_DATA_KEY);
        this.smsHasSent = false;
      }

      if (phone) return Promise.resolve(phone);

      return Register.getContact().then(
        ({
          body: {
            contact_info: { phone },
          },
        }) => {
          this.phone = phone;
        },
      );
    },

    sendSms() {
      this.errorMessage = null;
      this.isSendingSms = true;

      return Register.smsSend({ telephone_number: this.phone })
        .then(({ body: { time_to_wait } }) => {
          storage.writeJSON(STARGE_DATA_KEY, {
            phone: this.phone,
            exp: Date.now() + time_to_wait * 1000,
          });

          this.smsHasSent = true;
          this.createTimer(time_to_wait);

          return true;
        })
        .catch(({ body: { message } }) => {
          if (message) this.$message.error(message);
          this.errorMessage = message;
        })
        .finally(() => (this.isSendingSms = false));
    },

    sendSmsToNewNumber(newPhone) {
      const prevPhone = this.phone;
      this.phone = newPhone;

      this.sendSms().then(sent => {
        // revert if unable to send message
        if (!sent) this.phone = prevPhone;
        this.closeModal('editPhoneModal');
      });
    },
    submitSmsVerificationForm() {
      this.$emit('submitSmsVerification', this.code);
    },

    handleInput(e) {
      if (e.target.value?.length >= this.codeLength) {
        this.hideKeyboard();
      }
    },

    createTimer(seconds) {
      this.time = seconds || 30;
      this.timer = setInterval(this.timerTick, 1000);
    },

    timerTick() {
      if (this.time > 0) {
        this.time -= 1;
      } else {
        clearInterval(this.timer);
      }
    },

    hideKeyboard() {
      document.activeElement.blur();
      this.$refs.input.blur();
    },
  },
};
</script>

<style lang="scss">
.security-code {
  $security-code: &;
  $transition: 150ms ease;

  margin: 2 * $global-margin 0;

  &-container {
    padding: 0;
    margin-left: 0;
    justify-content: center;
    display: flex;
    align-items: center;

    font-family: $lb-body-en-font-family;
  }

  &__input {
    height: 40px;

    input {
      text-align: center;
      letter-spacing: 5px;
      text-indent: 5px;
    }
  }

  &__input-wrapper {
    height: 50px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__field {
    position: relative;

    display: block;
    height: 40px;
    width: 40px;
    margin: 2px 4px;

    list-style: none;
    line-height: 42px;
    font-size: 16px;
    text-align: center;
    color: #000;

    background-color: #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);

    transition: $transition;

    &--active {
      background-color: rgba($clouds, 0.4);

      #{$security-code}__char {
        transform: scale(1);
        opacity: 1;
      }
    }

    &:not(#{$security-code}__field--active)::after {
      position: absolute;
      left: 50%;

      content: '-';

      transform: translate(-50%);
    }
  }

  &__actions {
    text-align: center;

    button {
      margin: 0 !important;
      min-width: 150px;
    }
  }
}
</style>
